// The project-schematic hook (Brief 5 / pt7) — fetch the bound repo's structural
// map once per session and hold it for the read/write transparency surface. The
// backend is server-cached per repo+branch, so a re-open is cheap; this hook
// just owns the one read + its calm states. The wire is injected via `useApi()`.
//
// States are honest + non-alarm: `loading` while in flight; `ready` with the
// map; `unbound` when the session has no repo yet (a 409 — the connect flow's
// repo-bind hasn't happened); `reconnect` when the GitHub token expired (a 401 —
// the user reconnects GitHub); `error` for anything else. None of these block
// the session surface — the schematic is an additive panel.

import { useEffect, useState } from "react";
import { ApiError } from "@waytide/core";
import type { SchematicResponse } from "@waytide/core";
import { useApi } from "../api";

export type SchematicState =
  | { kind: "loading" }
  | { kind: "ready"; schematic: SchematicResponse }
  | { kind: "unbound" }
  | { kind: "reconnect" }
  | { kind: "error" };

/** Fetch + hold the bound repo's schematic for `threadId`. Re-fetches when the
 * thread changes; idle (no fetch) when `threadId` is null. */
export function useSchematic(threadId: number | null): SchematicState {
  const api = useApi();
  const [state, setState] = useState<SchematicState>({ kind: "loading" });

  useEffect(() => {
    if (threadId == null) {
      setState({ kind: "loading" });
      return;
    }
    let alive = true;
    setState({ kind: "loading" });
    void api
      .getSchematic(threadId)
      .then((schematic) => {
        if (alive) setState({ kind: "ready", schematic });
      })
      .catch((e: unknown) => {
        if (!alive) return;
        // 409 = the session isn't bound to a repo yet (calm, expected pre-bind).
        if (e instanceof ApiError && e.status === 409) {
          setState({ kind: "unbound" });
          return;
        }
        // 401 = the GitHub token needs reconnecting (the user fixes it).
        if (e instanceof ApiError && e.status === 401) {
          setState({ kind: "reconnect" });
          return;
        }
        setState({ kind: "error" });
      });
    return () => {
      alive = false;
    };
  }, [api, threadId]);

  return state;
}
